/* istanbul ignore file -- remove once more SB tests are added */
import { Heading } from "@snapchat/web-sds-c";
import { useIntl } from "react-intl";
import { useIsMounted } from "../../../../hooks/isMounted";
import { ImageTile } from "../../../common/ImageTile";
import { BlurredIconTile } from "../../../common/ImageTile/BlurredIconTile";
import { VideoTile } from "../../../common/ImageTile/VideoTile";
import { LensNameWithLensSuffix } from "../../../common/LensNameWithLensSuffix";
import PlaceHolder from "./icons/Placeholder.svg";
import styles from "./LensTile.module.css";
import { PageType } from "@snapchat/web-blizzard-reporting";
import { generateDeeplinkOrWebFallbackUrl } from "@snapchat/web-sc-cookies";
import { resize, WebPImage } from "@snapchat/web-core-ui";
export var LensTileSize;
(function (LensTileSize) {
    LensTileSize[LensTileSize["small"] = 0] = "small";
    LensTileSize[LensTileSize["medium"] = 1] = "medium";
    LensTileSize[LensTileSize["large"] = 2] = "large";
    LensTileSize[LensTileSize["elastic"] = 3] = "elastic";
})(LensTileSize || (LensTileSize = {}));
// 64 should be good enough, but for some reason, icons resized to 64 look really bad
const ICON_SIZE = 128;
const dimensionsBySize = {
    [LensTileSize.small]: {
        height: "197px",
        width: "",
    },
    [LensTileSize.medium]: {
        height: "230px",
        width: "137px",
    },
    [LensTileSize.large]: { height: "460px", width: "274px" },
    [LensTileSize.elastic]: {},
};
export function LensTile({ lensDisplayInfo, onClick, hideCreatorName, useBoldTitle, enableVideoPreview, size, lazyLoadImage = false, }) {
    const isMounted = useIsMounted();
    const dimensions = dimensionsBySize[size];
    const intl = useIntl();
    const alt = `${lensDisplayInfo.lensName} Lens and Filter by ${lensDisplayInfo.lensCreatorDisplayName} on Snapchat`;
    const showTilePreview = Boolean(!lensDisplayInfo.lensPreviewImageUrl && lensDisplayInfo.iconUrl);
    const showVideoPreview = isMounted && enableVideoPreview && lensDisplayInfo.lensPreviewVideoUrl;
    const renderPlaceholderOverlay = () => (<div className={styles.emptyTileMedia}>
      <span className={styles.placeHolder}>
        <PlaceHolder />
      </span>
    </div>);
    const renderImageTileOverlay = () => {
        if (!lensDisplayInfo.lensPreviewImageUrl && !lensDisplayInfo.iconUrl) {
            return renderPlaceholderOverlay();
        }
        else {
            // APS returns a 64x64 image that looks bad, so we force a 128x128 regardless of DPR
            const iconAsWebP = resize({
                imageUrl: lensDisplayInfo.iconUrl,
                width: ICON_SIZE,
                height: ICON_SIZE,
                applyDPR: false,
                format: "webp",
            });
            const iconAsPNG = resize({
                imageUrl: lensDisplayInfo.iconUrl,
                width: ICON_SIZE,
                height: ICON_SIZE,
                applyDPR: false,
                format: "png",
            });
            return (<div className={styles.tileIconContainer}>
          {lensDisplayInfo.iconUrl && (<WebPImage webpUrl={iconAsWebP} fallbackUrl={iconAsPNG} pictureProps={{ className: styles.tileIcon }} imgProps={{ alt, loading: lazyLoadImage ? "lazy" : "eager" }}/>)}
        </div>);
        }
    };
    const blurredTileAlt = intl.formatMessage({
        description: "Snapchat Lens Icon",
        defaultMessage: "Snapchat Lens Icon for {lensName} ",
    }, { lensName: lensDisplayInfo.lensName });
    const lensDeeplinkOrWebFallbackURL = generateDeeplinkOrWebFallbackUrl(lensDisplayInfo.unlockUrl, PageType[PageType.LENS_HOME_PAGE].toString().toLowerCase());
    return (<a href={lensDeeplinkOrWebFallbackURL} className={styles.link} onClick={onClick} data-testid="lensLink" style={{ width: dimensions.width }}>
      <div data-testid="lensImageTile">
        {showVideoPreview ? (<VideoTile {...dimensions} title={lensDisplayInfo.lensName} videoUrl={lensDisplayInfo.lensPreviewVideoUrl}>
            {renderImageTileOverlay()}
          </VideoTile>) : showTilePreview ? (<div style={dimensions}>
            <span className={styles.blurredImageRadius}>
              <BlurredIconTile iconUrl={lensDisplayInfo.iconUrl} previewImageUrl={lensDisplayInfo.iconUrl} size="small" alt={blurredTileAlt}/>
            </span>
          </div>) : (<ImageTile imageSrc={lensDisplayInfo.lensPreviewImageUrl || lensDisplayInfo.iconUrl} alt={lensDisplayInfo.lensName} {...dimensions} lazy={lazyLoadImage}>
            {renderImageTileOverlay()}
          </ImageTile>)}
      </div>
      <div className={styles.description} data-testid="lensDescription">
        <Heading size="H6" emphasis={useBoldTitle}>
          <div className={styles.lensName}>
            <LensNameWithLensSuffix lensName={lensDisplayInfo.lensName}/>
          </div>
        </Heading>

        {!hideCreatorName && (<Heading size="H6">
            <div className={styles.creatorName}>{lensDisplayInfo.lensCreatorDisplayName}</div>
          </Heading>)}
      </div>
    </a>);
}
